/**
 * AIS target ageing — a target keeps rendering "live" only while we're still
 * hearing from it. Real plotters and ECDIS don't just leave a stale contact
 * sitting there looking current, because a contact we stopped hearing from ten
 * minutes ago is a ghost: its position, course and speed are all guesses now, so
 * feeding it into CPA/TCPA raises alarms against a vessel that may be nowhere
 * near where we're drawing it. Instead they age it in stages.
 *
 * The stages, by how long since the last report:
 *   - live  : heard from recently, trust it fully, draw it normally.
 *   - stale : gone quiet for a bit, so we DIM it as a warning. Still plotted and
 *             still watched for CPA (it's aged, not abandoned).
 *   - lost  : silent long enough that we no longer trust it. Drawn crossed-out,
 *             the way a plotter shows a lost target, and pulled OUT of CPA so a
 *             ghost can't raise a collision alarm.
 *   - (drop): silent so long there's no point keeping it at all; it's removed.
 *
 * All pure and clock-injected (nowMs passed in), so the staging is easy to test.
 */
import type { AisTarget } from '@/store/ais';

/** Quiet for this long: dim the target as a "getting old" warning. */
export const AGE_STALE_MS = 90_000; // 1.5 min
/** Quiet for this long: treat as lost (crossed-out, dropped from CPA). */
export const AGE_LOST_MS = 360_000; // 6 min
/** Quiet for this long: remove the target entirely. */
export const AGE_DROP_MS = 720_000; // 12 min

export type AgeStatus = 'live' | 'stale' | 'lost';

/** Milliseconds since a target last reported (never negative). */
export function targetAgeMs(lastMs: number, nowMs: number): number {
  const age = nowMs - lastMs;
  return age > 0 ? age : 0;
}

/** Which ageing stage an age (ms since last report) falls into. */
export function ageStatus(ageMs: number): AgeStatus {
  if (ageMs >= AGE_LOST_MS) return 'lost';
  if (ageMs >= AGE_STALE_MS) return 'stale';
  return 'live';
}

/**
 * How far a target's colour has drained toward grey, 0 (fresh, full type colour)
 * to 1 (fully grey). We ramp it linearly from the first report to the lost
 * threshold, so a contact doesn't snap from colour to grey at a stage boundary but
 * bleeds towards it as we hear from it less — the age is visible in the colour, not
 * just in a dim step. It reaches full grey exactly at AGE_LOST_MS, which is where
 * we already treat it as lost (crossed out, dropped from CPA); it then holds grey
 * until AGE_DROP_MS removes it. Clamped to 0..1.
 */
export function ageGreyFrac(ageMs: number): number {
  if (ageMs <= 0) return 0;
  if (ageMs >= AGE_LOST_MS) return 1;
  return ageMs / AGE_LOST_MS;
}

/** Convenience: classify a target against the current clock. */
export function classifyTarget(t: AisTarget, nowMs: number): AgeStatus {
  return ageStatus(targetAgeMs(t.lastMs, nowMs));
}

/** True once a target has gone quiet long enough to distrust it for CPA. */
export function isLost(t: AisTarget, nowMs: number): boolean {
  return classifyTarget(t, nowMs) === 'lost';
}

/** True once a target has been silent long enough to remove it altogether. */
export function shouldDrop(ageMs: number): boolean {
  return ageMs >= AGE_DROP_MS;
}
